import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { DEFAULT_TYPEID_FALLBACK } from '@/common/utils/rowDataUtils'
import type { ExchangeData } from '@/services/action/types'

export type BoundDocumentIdentity = {
  _id: number
  _typeId: string
  _versionId?: number | null
}

/** PK документа на wire — не прикладные имена из meta. */
export const DOCUMENT_IDENTITY_FIELD_KEYS = ['_id', '_typeId', '_versionId'] as const

export function isDocumentIdentityFieldKey(key: string): boolean {
  return (DOCUMENT_IDENTITY_FIELD_KEYS as readonly string[]).includes(key)
}

function readExchangeScalarLong(fields: Record<string, unknown>, name: string): number | null {
  const raw = fields[name]
  if (raw == null) return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'object' && raw !== null && 'value' in (raw as object)) {
    const value = (raw as { value: unknown }).value
    if (value == null || value === '') return null
    const num = Number(value)
    return Number.isFinite(num) ? num : null
  }
  const num = Number(raw)
  return Number.isFinite(num) ? num : null
}

function readExchangeScalarString(fields: Record<string, unknown>, name: string): string {
  const raw = fields[name]
  if (raw == null) return ''
  if (typeof raw === 'string') return raw
  if (typeof raw === 'object' && raw !== null && 'value' in (raw as object)) {
    const value = (raw as { value: unknown }).value
    return value == null ? '' : String(value)
  }
  return String(raw)
}

export function readExchangeDocumentIdentity(
  fields: Record<string, unknown>,
): BoundDocumentIdentity | null {
  const id = readExchangeScalarLong(fields, '_id')
  const typeId = readExchangeScalarString(fields, '_typeId').trim()
  if (id == null || id < 0 || !typeId) return null
  if (DEFAULT_TYPEID_FALLBACK.placeholderValues.includes(typeId)) return null
  const versionId = readExchangeScalarLong(fields, '_versionId')
  return {
    _id: id,
    _typeId: typeId,
    ...(versionId != null ? { _versionId: versionId } : {}),
  }
}

function readPlainDocumentIdentity(row: Record<string, unknown>): BoundDocumentIdentity | null {
  const idRaw = row._id
  const id =
    typeof idRaw === 'number' && Number.isFinite(idRaw)
      ? idRaw
      : idRaw != null && idRaw !== ''
        ? Number(idRaw)
        : null
  const typeId = String(row._typeId ?? row.typeId ?? '').trim()
  if (id == null || !Number.isFinite(id) || id < 0 || !typeId) return null
  if (DEFAULT_TYPEID_FALLBACK.placeholderValues.includes(typeId)) return null
  const versionRaw = row._versionId
  const versionId =
    versionRaw == null || versionRaw === ''
      ? undefined
      : typeof versionRaw === 'number' && Number.isFinite(versionRaw)
        ? versionRaw
        : Number(versionRaw)
  return {
    _id: id,
    _typeId: typeId,
    ...(versionId != null && Number.isFinite(versionId) ? { _versionId: versionId } : {}),
  }
}

export function buildBoundDocumentIdentityFromRow(
  row: Record<string, unknown> | null | undefined,
): BoundDocumentIdentity | null {
  if (!row || typeof row !== 'object') return null

  const fromFields =
    row.fields && typeof row.fields === 'object'
      ? readExchangeDocumentIdentity(row.fields as Record<string, unknown>)
      : null
  if (fromFields) return fromFields

  return readPlainDocumentIdentity(row)
}

export function resolveBoundDocumentIdentityFromPage(
  pageData: unknown,
): BoundDocumentIdentity | null {
  if (!pageData || typeof pageData !== 'object') return null
  const bound = (pageData as Record<string, unknown>).$boundDocumentIdentity
  if (bound && typeof bound === 'object') {
    return readPlainDocumentIdentity(bound as Record<string, unknown>)
  }
  return null
}

export function boundDocumentIdentityChanged(
  oldPageData: unknown,
  newPageData: unknown,
): boolean {
  const oldBound = resolveBoundDocumentIdentityFromPage(oldPageData)
  const newBound = resolveBoundDocumentIdentityFromPage(newPageData)
  if (!oldBound && !newBound) return false
  if (!oldBound || !newBound) return Boolean(oldBound || newBound)
  if (oldBound._id !== newBound._id) return true
  if (oldBound._typeId !== newBound._typeId) return true
  const oldVersion = oldBound._versionId ?? null
  const newVersion = newBound._versionId ?? null
  return oldVersion !== newVersion
}

/** Identity из uniData/changed_data страницы (fallback, если $boundDocumentIdentity не записан). */
export function resolveDocumentIdentityFromPageSnapshot(
  pageData: unknown,
): BoundDocumentIdentity | null {
  if (!pageData || typeof pageData !== 'object') return null
  const fields = mergePageDataFieldSnapshot(pageData as Record<string, unknown>)
  return readExchangeDocumentIdentity(fields)
}

function identityFieldValue(identity: BoundDocumentIdentity, key: '_id' | '_typeId' | '_versionId') {
  if (key === '_id') {
    return { value: identity._id, type: 'Long' as const }
  }
  if (key === '_typeId') {
    return { value: identity._typeId, type: 'String' as const }
  }
  if (identity._versionId != null) {
    return { value: identity._versionId, type: 'Long' as const }
  }
  return null
}

export function boundDocumentIdentityToFieldMap(
  bound: BoundDocumentIdentity | null | undefined,
): Record<string, unknown> {
  if (!bound) return {}
  const out: Record<string, unknown> = {}
  for (const key of ['_id', '_typeId', '_versionId'] as const) {
    const next = identityFieldValue(bound, key)
    if (next) {
      out[key] = next
    }
  }
  return out
}


export function eventsLayoutReloadFormFields(pageData: unknown): Record<string, unknown> {
  return boundDocumentIdentityToFieldMap(resolveOpenedDocumentIdentity(pageData))
}

function readUniDataFieldMap(pageData: unknown): Record<string, unknown> | null {
  if (!pageData || typeof pageData !== 'object') return null
  const fields = (pageData as { uniData?: { fields?: { fields?: unknown } } }).uniData?.fields
    ?.fields
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return null
  return fields as Record<string, unknown>
}

/**
 * Document the form was opened on.
 * `$boundDocumentIdentity` when the open was a document transfer.
 * Otherwise uniData from the first response (`convert.document`).
 * Not `changed_data`: a checkbox procedure can already have written a service id there.
 */
export function resolveOpenedDocumentIdentity(pageData: unknown): BoundDocumentIdentity | null {
  return (
    resolveBoundDocumentIdentityFromPage(pageData) ??
    readExchangeDocumentIdentity(readUniDataFieldMap(pageData) ?? {})
  )
}

function wrapLooseIdentityScalar(
  key: '_id' | '_typeId' | '_versionId',
  raw: unknown,
): { type: string; value: unknown } | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'object' && 'value' in (raw as object)) {
    const value = (raw as { value: unknown }).value
    if (value == null || value === '') return null
    return raw as { type: string; value: unknown }
  }
  if (key === '_typeId') {
    const text = String(raw).trim()
    return text ? { type: 'String', value: text } : null
  }
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? { type: 'Long', value: num } : null
}

/**
 * `_id` / `_versionId` / `_typeId` для Identificator.getData и panel currentData:
 * `$boundDocumentIdentity` (в т.ч. без `_typeId`) или snapshot uniData.
 */
export function readPageBoundIdentityFieldMap(pageData: unknown): Record<string, unknown> {
  const resolved = boundDocumentIdentityToFieldMap(
    resolveBoundDocumentIdentityFromPage(pageData) ??
      resolveDocumentIdentityFromPageSnapshot(pageData),
  )
  if (Object.keys(resolved).length > 0) return resolved
  if (!pageData || typeof pageData !== 'object') return {}
  const bound = (pageData as Record<string, unknown>).$boundDocumentIdentity
  if (!bound || typeof bound !== 'object') return {}
  const rec = bound as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of ['_id', '_typeId', '_versionId'] as const) {
    const next = wrapLooseIdentityScalar(key, rec[key])
    if (next) out[key] = next
  }
  return out
}

export function overlayBoundDocumentIdentityOnFields(
  fields: Record<string, unknown>,
  bound: BoundDocumentIdentity,
): void {
  const payloadType = readExchangeScalarString(fields, '_typeId').trim()
  if (!bound._typeId || payloadType === bound._typeId) return

  for (const key of ['_id', '_typeId', '_versionId'] as const) {
    const next = identityFieldValue(bound, key)
    if (next) {
      fields[key] = next
    } else if (key === '_versionId') {
      delete fields._versionId
    }
  }
}

export type PreservePayloadDocumentIdentityContext = {
  tilesRowScope?: unknown
  selectedData?: unknown
  /** Identity из uniData/changed_data слота формы action (не inherited `$boundDocumentIdentity`). */
  layoutDocumentIdentity?: BoundDocumentIdentity | null
}

function boundDocumentIdentitiesMatch(
  left: BoundDocumentIdentity | null | undefined,
  right: BoundDocumentIdentity | null | undefined,
): boolean {
  if (!left || !right) return false
  if (left._id !== right._id) return false
  if (left._typeId !== right._typeId) return false
  return (left._versionId ?? null) === (right._versionId ?? null)
}

/**
 * Tiles row / call&doc: payload intentionally carries row document (pd-service),
 * page shell carries parent document (pd-order). Do not overlay page bound onto payload.
 */
export function shouldPreservePayloadDocumentIdentityOverPageBound(
  payloadIdentity: BoundDocumentIdentity | null | undefined,
  pageBound: BoundDocumentIdentity | null | undefined,
  context?: PreservePayloadDocumentIdentityContext | null,
): boolean {
  if (!payloadIdentity || !pageBound) return false
  if (payloadIdentity._typeId === pageBound._typeId) return false

  if (context?.tilesRowScope) return true

  const fromSelected = buildBoundDocumentIdentityFromRow(
    context?.selectedData as Record<string, unknown> | null | undefined,
  )
  if (boundDocumentIdentitiesMatch(fromSelected, payloadIdentity)) {
    return true
  }

  if (boundDocumentIdentitiesMatch(payloadIdentity, context?.layoutDocumentIdentity)) {
    return true
  }

  return false
}

export function overlayBoundDocumentIdentityOnFieldsUnlessPreserved(
  fields: Record<string, unknown>,
  pageBound: BoundDocumentIdentity,
  context?: PreservePayloadDocumentIdentityContext | null,
): void {
  const payloadIdentity = readExchangeDocumentIdentity(fields)
  if (shouldPreservePayloadDocumentIdentityOverPageBound(payloadIdentity, pageBound, context)) {
    return
  }
  overlayBoundDocumentIdentityOnFields(fields, pageBound)
}

export function restoreDocumentIdentityFieldsIfTypeLeaked(
  mergedFields: Record<string, unknown>,
  bound: BoundDocumentIdentity,
): Record<string, unknown> {
  const mergedType = readExchangeScalarString(mergedFields, '_typeId').trim()
  if (!mergedType || mergedType === bound._typeId) {
    return mergedFields
  }

  const next = { ...mergedFields }
  for (const key of ['_id', '_typeId', '_versionId'] as const) {
    const field = identityFieldValue(bound, key)
    if (field) {
      next[key] = field
    } else if (key === '_versionId') {
      delete next._versionId
    }
  }
  return next
}

/**
 * Swing `incomingData.replace(procedure)` для PK: identity из ответа procedure
 * всегда перекрывает снимок / bound / table-bleed.
 */
export function replaceDocumentIdentityFieldsFromSource(
  mergedFields: Record<string, unknown>,
  sourceFields: Record<string, unknown>,
): Record<string, unknown> {
  const identity = readExchangeDocumentIdentity(sourceFields)
  if (!identity) return mergedFields
  const next = { ...mergedFields }
  for (const key of DOCUMENT_IDENTITY_FIELD_KEYS) {
    const field = identityFieldValue(identity, key)
    if (field) {
      next[key] = field
    } else if (key === '_versionId') {
      delete next._versionId
    }
  }
  return next
}

export type RestoreDocumentIdentityAfterProcedureMergeOptions = {
  allowCrossTypeRestore?: boolean
}

export function restoreDocumentIdentityFieldsAfterProcedureMerge(
  mergedFields: Record<string, unknown>,
  bound: BoundDocumentIdentity,
  _beforeProcedureFields: Record<string, unknown>,
  options?: RestoreDocumentIdentityAfterProcedureMergeOptions,
): Record<string, unknown> {
  const allowCrossTypeRestore = options?.allowCrossTypeRestore !== false
  const mergedType = readExchangeScalarString(mergedFields, '_typeId').trim()
  if (mergedType && mergedType !== bound._typeId) {
    if (!allowCrossTypeRestore) {
      return mergedFields
    }
    return restoreDocumentIdentityFieldsIfTypeLeaked(mergedFields, bound)
  }

  if (!mergedType || mergedType !== bound._typeId) {
    return mergedFields
  }

  const mergedId = readExchangeScalarLong(mergedFields, '_id')
  if (mergedId === 0 && bound._id > 0) {
    const next = { ...mergedFields }
    const idField = identityFieldValue(bound, '_id')
    if (idField) {
      next._id = idField
    }
    const versionField = identityFieldValue(bound, '_versionId')
    if (versionField) {
      next._versionId = versionField
    } else {
      delete next._versionId
    }
    return next
  }

  return mergedFields
}

/**
 * doAction for call_chain_*_change_doc reloads the document from client `_id`.
 * A schema replace can leave the action payload without identity while
 * `$boundDocumentIdentity` still has the service. Fill only that hole.
 * A different positive id or type stays as the caller wrote it.
 */
export function fillAbsentDocumentIdentityFromBound(
  fields: Record<string, unknown>,
  bound: BoundDocumentIdentity,
): Record<string, unknown> {
  if (!bound || bound._id <= 0 || !bound._typeId) return fields
  const payloadType = readExchangeScalarString(fields, '_typeId').trim()
  const payloadId = readExchangeScalarLong(fields, '_id')
  if (payloadType && payloadType !== bound._typeId) return fields
  if (payloadId != null && payloadId > 0 && payloadId !== bound._id) return fields
  if (
    payloadId != null &&
    payloadId > 0 &&
    payloadType === bound._typeId &&
    (readExchangeScalarLong(fields, '_versionId') != null || bound._versionId == null)
  ) {
    return fields
  }

  const next = { ...fields }
  for (const key of ['_id', '_typeId', '_versionId'] as const) {
    if (key === '_id' && payloadId != null && payloadId > 0) continue
    if (key === '_typeId' && payloadType) continue
    if (key === '_versionId' && readExchangeScalarLong(fields, '_versionId') != null) continue
    const field = identityFieldValue(bound, key)
    if (field) next[key] = field
    else if (key === '_versionId') delete next._versionId
  }
  return next
}

function selectedRowRecord(selectedData: unknown): Record<string, unknown> | null {
  const row = Array.isArray(selectedData) ? selectedData[0] : selectedData
  if (!row || typeof row !== 'object') return null
  return row as Record<string, unknown>
}

function selectedRowDocumentId(row: Record<string, unknown>): number | null {
  const identity = buildBoundDocumentIdentityFromRow(row)
  if (identity) return identity._id
  const nested = row.fields
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const fromFields = readExchangeScalarLong(nested as Record<string, unknown>, '_id')
    if (fromFields != null && fromFields >= 0) return fromFields
  }
  const plain = readExchangeScalarLong(row, '_id')
  return plain != null && plain >= 0 ? plain : null
}

function selectedRowDocumentType(row: Record<string, unknown>): string {
  const identity = buildBoundDocumentIdentityFromRow(row)
  if (identity?._typeId) return identity._typeId
  const nested = row.fields
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const fromFields = readExchangeScalarString(nested as Record<string, unknown>, '_typeId').trim()
    if (fromFields) return fromFields
  }
  return readExchangeScalarString(row, '_typeId').trim()
}

/**
 * POST /platform/reload/form replaces the core UniData.
 * An EdiTable onChange row must not become the form document: panel onLoad
 * then rebuilds tables for that id and the form hides an empty table.
 * Swing events reload does not send the JSContext row identity.
 */
export function restoreReloadFormDocumentIdentity(
  fields: Record<string, unknown>,
  pageData: unknown,
  selectedData: unknown,
): Record<string, unknown> {
  const bound = resolveOpenedDocumentIdentity(pageData)
  if (!bound || bound._id < 0) return fields
  const payloadId = readExchangeScalarLong(fields, '_id')
  const payloadType = readExchangeScalarString(fields, '_typeId').trim()
  // Events reload used to send nothing when $bound was missing. replace()
  // does not delete keys, so a procedure-written service id stayed on the core
  // and panel 4270 summed that id.
  if (payloadId == null) {
    const next = { ...fields }
    for (const key of DOCUMENT_IDENTITY_FIELD_KEYS) {
      const field = identityFieldValue(bound, key)
      if (field) next[key] = field
      else if (key === '_versionId') delete next._versionId
    }
    return next
  }
  if (
    payloadId != null &&
    payloadId === bound._id &&
    (!payloadType || !bound._typeId || payloadType === bound._typeId)
  ) {
    return fields
  }
  // pd-order 100 -> pd-order 200 is a real document switch. A consent service
  // (or the site-check row) is not: panel onLoad would query that id as the order.
  if (
    payloadId != null &&
    payloadType &&
    bound._typeId &&
    payloadType === bound._typeId &&
    payloadId !== bound._id
  ) {
    return fields
  }

  const row = selectedRowRecord(selectedData)
  const rowId = row ? selectedRowDocumentId(row) : null
  const rowType = row ? selectedRowDocumentType(row) : ''
  const payloadIsSelectedRow =
    payloadId != null &&
    rowId != null &&
    payloadId === rowId &&
    (!rowType || !payloadType || payloadType === rowType)
  const payloadTypeDiffers = Boolean(
    payloadType && bound._typeId && payloadType !== bound._typeId,
  )
  if (!payloadIsSelectedRow && !payloadTypeDiffers) return fields

  const next = { ...fields }
  for (const key of DOCUMENT_IDENTITY_FIELD_KEYS) {
    const field = identityFieldValue(bound, key)
    if (field) next[key] = field
    else if (key === '_versionId') delete next._versionId
  }
  return next
}

/** True when reload/form currentData is already the page document. */
export function reloadRequestUsesBoundDocument(
  fields: Record<string, unknown>,
  pageData: unknown,
): boolean {
  const bound = resolveOpenedDocumentIdentity(pageData)
  if (!bound || bound._id < 0) return true
  const payloadId = readExchangeScalarLong(fields, '_id')
  const payloadType = readExchangeScalarString(fields, '_typeId').trim()
  if (payloadId == null || payloadId !== bound._id) return false
  if (payloadType && bound._typeId && payloadType !== bound._typeId) return false
  return true
}

/**
 * First open of a non-document transfer can leave the order id in uniData
 * without `$boundDocumentIdentity`. Pin it once so a later service id in
 * changed_data cannot become the document reloadPanel sums.
 */
export function pinOpenedDocumentIdentity(pageData: unknown, selectedData?: unknown): void {
  if (!pageData || typeof pageData !== 'object') return
  if (resolveBoundDocumentIdentityFromPage(pageData)) return
  const opened = readExchangeDocumentIdentity(readUniDataFieldMap(pageData) ?? {})
  if (!opened) return
  const row = selectedRowRecord(selectedData)
  if (row) {
    const rowId = selectedRowDocumentId(row)
    const rowType = selectedRowDocumentType(row)
    if (
      rowId != null &&
      rowId === opened._id &&
      (!rowType || !opened._typeId || rowType === opened._typeId)
    ) {
      return
    }
  }
  ;(pageData as Record<string, unknown>).$boundDocumentIdentity = {
    _id: opened._id,
    _typeId: opened._typeId,
    ...(opened._versionId != null ? { _versionId: opened._versionId } : {}),
  }
}

/** _id / _typeId изменились между двумя field-map (procedure merge, doAction payload). */
export function documentIdentityPairChanged(
  beforeFields: Record<string, unknown>,
  afterFields: Record<string, unknown>,
): boolean {
  const afterId = readExchangeScalarLong(afterFields, '_id')
  const afterType = readExchangeScalarString(afterFields, '_typeId')
  if (afterId == null && afterType === '') {
    return false
  }
  const beforeId = readExchangeScalarLong(beforeFields, '_id')
  const beforeType = readExchangeScalarString(beforeFields, '_typeId')
  return beforeId !== afterId || beforeType !== afterType
}

export function documentIdentityChangedAfterPreAction(
  initialClientData: ExchangeData | undefined,
  mergedClientData: ExchangeData | undefined,
): boolean {
  const beforeFields = initialClientData?.fields?.fields ?? {}
  const afterFields = mergedClientData?.fields?.fields ?? {}
  return documentIdentityPairChanged(beforeFields, afterFields)
}

export function documentIdentityChangedOnPage(
  pageData: Record<string, unknown> | undefined,
  actionData: ExchangeData | undefined,
): boolean {
  const pageFields = mergePageDataFieldSnapshot(pageData)
  const actionFields = actionData?.fields?.fields ?? {}
  return documentIdentityPairChanged(pageFields, actionFields)
}
